import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";

import { mutateData } from "@/core/api/api-methods";
import type { Paged } from "@/core/api/pagination";
import type { SearchableSelectOption } from "@/core/components/ui/searchable-select";
import { MAX_PAGE_SIZE } from "@/core/utils/func/input-normalization";

/** Corps de liste pour alimenter un select : une page au maximum autorisé. */
export const ENTITY_SELECT_LIST_PARAMS = {
  page: 1,
  pageSize: MAX_PAGE_SIZE,
} as const;

/**
 * Liste paginée `POST .../all` (guide §4.2) projetée en options de
 * `SearchableSelect`. L’URL est fournie par l’appelant (endpoints de
 * feature ré-exportés par core) — ce hook n’importe aucune feature.
 */
export function usePagedSelectOptions<T>(args: {
  queryKey: readonly unknown[];
  url: string;
  mapItem: (item: T) => SearchableSelectOption | null;
  enabled?: boolean;
  /**
   * Prédicat optionnel appliqué à chaque item avant mapItem — permet de
   * restreindre les options côté client (ex. filtrer les portefeuilles par
   * `keycloakUserId` quand le filtre serveur ne l'expose pas, cf.
   * `WalletFilter` qui ne couvre que party/type/status).
   */
  filterItem?: (item: T) => boolean;
}): {
  options: SearchableSelectOption[];
  isLoading: boolean;
} {
  const enabled = args.enabled ?? true;
  const { data, isLoading } = useQuery({
    queryKey: args.queryKey,
    queryFn: () =>
      mutateData({
        url: args.url,
        reqBody: ENTITY_SELECT_LIST_PARAMS as unknown as Record<string, unknown>,
        method: "POST",
      }) as Promise<Paged<T>>,
    enabled,
  });

  const options = useMemo(() => {
    const mapped: SearchableSelectOption[] = [];
    const seen = new Set<string>();
    for (const item of data?.items ?? []) {
      if (args.filterItem && !args.filterItem(item)) continue;
      const option = args.mapItem(item);
      if (!option?.value || seen.has(option.value)) continue;
      seen.add(option.value);
      mapped.push(option);
    }
    return mapped;
    // mapItem / filterItem : fonctions module-level stables chez les appelants.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- items + filterItem (si fourni) : évite un rerender boucle si mapItem est recréé.
  }, [data?.items, args.filterItem]);

  return { options, isLoading: enabled && isLoading };
}
